import { useCallback } from 'react'

import {
  type InfiniteData,
  type QueryClient,
  useInfiniteQuery,
} from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { buildPaginatedEndpoint, getNextPageParam } from '@/helpers/api'
import { SweetspotError } from '@/helpers/error'
import { fuzzyMatch } from '@/helpers/string'
import { compareTagsByPosition } from '@/helpers/tags'

import { tagsQueryKeys } from '@/hooks/tags/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginateRequest, PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import { CONVENTIONAL_TAG_TYPES, type Tag, type TagType } from '@/types/Tag'

interface UseTagsArgs<T> {
  orgId: UUID | ''
  search?: string
  tagIds?: UUID[]
  tagType?: TagType | null
  tagTypes?: TagType[] | null
  selector?: (tags: InfiniteData<PaginatedResponse<Tag>, PaginateRequest>) => T
}

const filterTagsByType = (
  tags: Tag[],
  tagType: TagType | null | undefined,
  tagTypes: TagType[] | null | undefined,
) => {
  if (tagType) {
    return tags.filter((tag) => tag.type === tagType)
  }

  if (tagTypes) {
    return tags.filter((tag) => tagTypes.includes(tag.type))
  }

  return tags.filter((tag) => CONVENTIONAL_TAG_TYPES.includes(tag.type))
}

export const useTags = <T = Tag[]>({
  orgId,
  search,
  tagIds,
  tagType = 'organization',
  tagTypes,
  selector,
}: UseTagsArgs<T>) => {
  const { accessToken } = useAuthInfoWithThrow()

  const select = useCallback(
    (data: InfiniteData<PaginatedResponse<Tag>, PaginateRequest>): T => {
      const allTags = data.pages.flatMap((res) => res.data)
      const matchingTags = filterTagsByType(allTags, tagType, tagTypes)

      if (tagIds) {
        return matchingTags.filter((tag) =>
          tagIds.includes(tag.id),
        ) as unknown as T
      }

      const filteredData = {
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          data: filterTagsByType(page.data, tagType, tagTypes),
        })),
      }

      if (selector) {
        return selector(filteredData) as T
      }

      let tags = filteredData.pages.flatMap((res) => res.data)
      if (!search) return tags as unknown as T
      const searchLower = search.toLowerCase()
      return tags
        .filter((tag) => fuzzyMatch(tag.name.toLowerCase(), searchLower))
        .sort(compareTagsByPosition) as unknown as T
    },
    [search, selector, tagIds, tagType, tagTypes],
  )

  return useInfiniteQuery({
    queryFn: ({ pageParam }) => fetchTags(orgId, accessToken, pageParam),
    queryKey: tagsQueryKeys.organization({ orgId }),
    staleTime: Infinity,
    enabled: !!orgId,
    initialPageParam: { limit: 10000 }, // TODO
    getNextPageParam: getNextPageParam,
    select,
  })
}

export const fetchTags = async (
  orgId: UUID | '',
  accessToken: string,
  pagination: PaginateRequest,
): Promise<PaginatedResponse<Tag>> => {
  if (!orgId) {
    throw new Error('Organization ID is required')
  }
  const url = buildPaginatedEndpoint(
    `${API_URL}/api/v1/organizations/${orgId}/tags`,
    pagination,
  )
  const res: Response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: {
        status: res.status,
      },
    })
  }
  return (await res.json()) as PaginatedResponse<Tag>
}

/**
 * Imperative counterpart to `useTags` for event handlers that need tags
 * without keeping a per-row query observer mounted. Reads the same cache
 * entry; fetches it if cold.
 */
export const ensureTagsByType = async (
  queryClient: QueryClient,
  orgId: UUID | '',
  accessToken: string,
  tagType: TagType,
): Promise<Tag[]> => {
  const data = await queryClient.ensureInfiniteQueryData({
    queryKey: tagsQueryKeys.organization({ orgId }),
    queryFn: ({ pageParam }: { pageParam: PaginateRequest }) =>
      fetchTags(orgId, accessToken, pageParam),
    initialPageParam: { limit: 10000 } as PaginateRequest,
    staleTime: Infinity,
  })
  const allTags = data.pages.flatMap((page) => page.data)
  return filterTagsByType(allTags, tagType, null)
}
